if (!customElements.get('product-form')) {
  const optionSightProductVariantPriceRequests = new Map();

  async function loadOptionSightProductVariantPrices(url) {
    const sourceUrl = String(url || '').trim();
    if (!sourceUrl) throw new Error('OptionSight product URL is missing.');
    if (!optionSightProductVariantPriceRequests.has(sourceUrl)) {
      const request = fetch(sourceUrl, {
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        cache: 'no-store',
      })
        .then(async (response) => {
          if (!response.ok) throw new Error('OptionSight auxiliary pricing request failed.');
          const product = await response.json();
          const prices = new Map(
            (Array.isArray(product?.variants) ? product.variants : [])
              .filter((variant) => variant?.available !== false)
              .map((variant) => [String(variant.id || ''), Number(variant.price)])
              .filter(
                ([variantId, priceMinor]) =>
                  /^\d+$/.test(variantId) && Number.isSafeInteger(priceMinor) && priceMinor >= 0
              )
          );
          if (prices.size === 0) throw new Error('OptionSight auxiliary pricing is unavailable.');
          return prices;
        })
        .catch((error) => {
          optionSightProductVariantPriceRequests.delete(sourceUrl);
          throw error;
        });
      optionSightProductVariantPriceRequests.set(sourceUrl, request);
    }
    return optionSightProductVariantPriceRequests.get(sourceUrl);
  }

  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.hasAttribute('data-optionsight-managed-checkout')) {
          evt.stopImmediatePropagation?.();
          this.handleErrorMessage('Custom options are still loading. Please refresh and try again.');
          this.emitOptionSightIntegrityDiagnostic('managed-fallback', 'blocked', {
            reason: 'optionsight_submit_capture_missing',
          });
          return;
        }
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        let optionSightPayload = null;
        this.optionSightQuickAddIntegrity = null;
        try {
          optionSightPayload = await this.getOptionSightQuickAddPayload(formData);
        } catch (error) {
          console.error(error);
          this.emitOptionSightIntegrityDiagnostic('quick-add-preflight', 'blocked', {
            reason: 'live_configuration_unavailable',
          });
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner').classList.add('hidden');
          if (this.dataset.productUrl) window.location = this.dataset.productUrl;
          return;
        }
        if (optionSightPayload && this.optionSightQuickAddIntegrity) {
          this.emitOptionSightIntegrityDiagnostic('quick-add-preflight', 'passed', {
            groupId: this.optionSightQuickAddIntegrity.expectation.groupId,
            expectedLineCount: this.optionSightQuickAddIntegrity.expectation.items.length,
            expectedAmountMinor: this.optionSightQuickAddIntegrity.expectation.originalAmountMinor,
          });
        }
        if (this.cart) {
          const sections = this.cart.getSectionsToRender().map((section) => section.id);
          if (optionSightPayload) {
            optionSightPayload.sections = sections;
            optionSightPayload.sections_url = window.location.pathname;
          } else {
            formData.append('sections', sections);
            formData.append('sections_url', window.location.pathname);
          }
          this.cart.setActiveElement(document.activeElement);
        }

        if (optionSightPayload) {
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify(optionSightPayload);
        } else {
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              if (optionSightPayload) {
                const integrityError = new Error('OptionSight quick-add request was rejected.');
                integrityError.optionsightReason = 'cart_add_rejected';
                throw integrityError;
              }
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            if (optionSightPayload) {
              const cartSnapshot = await this.fetchOptionSightCartSnapshot();
              this.optionSightQuickAddIntegrity.cartSnapshot = cartSnapshot;
              const verification = this.optionSightQuickAddIntegrity.quickAddModule.verifyOptionSightQuickAddCart(
                cartSnapshot,
                this.optionSightQuickAddIntegrity.expectation
              );
              if (!verification.ok) {
                const integrityError = new Error('OptionSight quick-add checkout integrity failure.');
                integrityError.optionsightReason = verification.reason;
                throw integrityError;
              }
              this.emitOptionSightIntegrityDiagnostic('quick-add-verify', 'passed', {
                groupId: this.optionSightQuickAddIntegrity.expectation.groupId,
                expectedLineCount: this.optionSightQuickAddIntegrity.expectation.items.length,
                actualLineCount: verification.actualLineCount,
                expectedAmountMinor: this.optionSightQuickAddIntegrity.expectation.originalAmountMinor,
                actualAmountMinor: verification.actualAmountMinor,
              });
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(this.normalizeOptionSightQuickAddResponse(response));
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(this.normalizeOptionSightQuickAddResponse(response));
            }
          })
          .catch(async (error) => {
            if (optionSightPayload && this.optionSightQuickAddIntegrity) {
              const rollback = await this.rollbackOptionSightQuickAddGroup();
              this.emitOptionSightIntegrityDiagnostic('quick-add', 'failed', {
                reason: String(error?.optionsightReason || 'quick_add_integrity_failed'),
                groupId: this.optionSightQuickAddIntegrity.expectation.groupId,
                rollbackConfirmed: rollback.confirmed,
              });
              this.handleErrorMessage(
                rollback.confirmed
                  ? 'Custom-option charges could not be verified, so this item was not added.'
                  : 'Custom-option charges could not be verified. Please review your cart.'
              );
              if (this.dataset.productUrl) window.location.assign(this.dataset.productUrl);
              return;
            }
            console.error(error);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      async getOptionSightQuickAddPayload(formData) {
        if (!this.hasAttribute('data-optionsight-quick-add')) return null;
        const moduleUrl = String(this.dataset.optionsightQuickAddScriptUrl || '').trim();
        const auxiliaryProductUrls = String(this.dataset.optionsightAuxiliaryProductUrls || '')
          .split(',')
          .map((url) => url.trim())
          .filter(Boolean);
        const parentProductUrl = String(this.dataset.optionsightParentProductUrl || '').trim();
        const urls = String(this.dataset.optionsightOptionConfigUrls || '')
          .split(',')
          .map((url) => url.trim())
          .filter(Boolean);
        if (!moduleUrl) throw new Error('OptionSight quick-add script is missing.');
        if (urls.length === 0) throw new Error('OptionSight quick-add configuration is missing.');
        if (!parentProductUrl) throw new Error('OptionSight quick-add parent pricing URL is missing.');

        const [quickAddModule, configs, auxiliaryVariantPriceMaps, parentVariantPricesMinor] = await Promise.all([
          import(moduleUrl),
          Promise.all(
            urls.map(async (url) => {
              const response = await fetch(url);
              if (!response.ok) throw new Error(`Unable to load OptionSight quick-add configuration: ${url}`);
              return response.json();
            })
          ),
          Promise.all(auxiliaryProductUrls.map((url) => loadOptionSightProductVariantPrices(url))),
          loadOptionSightProductVariantPrices(parentProductUrl),
        ]);
        const variantPricesMinor = new Map(
          auxiliaryVariantPriceMaps.flatMap((variantPriceMap) => [...variantPriceMap.entries()])
        );
        const payload = quickAddModule.createOptionSightQuickAddPayload(
          formData,
          configs.flatMap((config) => config.rows || []),
          this.dataset.optionsightOptionSetIds || '',
          this.dataset.optionsightPropertyName || 'Selected custom options',
          variantPricesMinor
        );
        const parentPriceMinor = parentVariantPricesMinor.get(String(formData.get('id') || ''));
        const expectation = quickAddModule.createOptionSightQuickAddExpectation(
          payload,
          parentPriceMinor,
          variantPricesMinor
        );
        this.optionSightQuickAddIntegrity = { quickAddModule, expectation };
        return payload;
      }

      getOptionSightCartJsonUrl() {
        const cartUrl = String(window.routes?.cart_url || '/cart').replace(/\/$/, '');
        return cartUrl.endsWith('.js') ? cartUrl : `${cartUrl}.js`;
      }

      async fetchOptionSightCartSnapshot() {
        const response = await fetch(this.getOptionSightCartJsonUrl(), {
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          cache: 'no-store',
        });
        if (!response.ok) throw new Error('OptionSight cart verification request failed.');
        const cart = await response.json();
        if (!Array.isArray(cart?.items)) throw new Error('OptionSight cart verification response is invalid.');
        return cart;
      }

      async rollbackOptionSightQuickAddGroup() {
        const integrity = this.optionSightQuickAddIntegrity;
        if (!integrity?.expectation?.groupId) return { confirmed: false };
        try {
          const cart = integrity.cartSnapshot || (await this.fetchOptionSightCartSnapshot());
          const groupLines = integrity.quickAddModule.getOptionSightQuickAddGroupLines(
            cart,
            integrity.expectation.groupId
          );
          const updates = {};
          groupLines.forEach((line) => {
            if (line?.key) updates[line.key] = 0;
          });
          if (Object.keys(updates).length > 0) {
            const response = await fetch(routes.cart_update_url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: JSON.stringify({ updates }),
            });
            if (!response.ok) throw new Error('OptionSight quick-add rollback failed.');
          }
          const confirmedCart = await this.fetchOptionSightCartSnapshot();
          const remaining = integrity.quickAddModule.getOptionSightQuickAddGroupLines(
            confirmedCart,
            integrity.expectation.groupId
          );
          return { confirmed: groupLines.length > 0 && remaining.length === 0 };
        } catch {
          return { confirmed: false };
        }
      }

      emitOptionSightIntegrityDiagnostic(stage, status, detail = {}) {
        const diagnostic = {
          version: 1,
          stage,
          status,
          mode: this.hasAttribute('data-optionsight-quick-add') ? 'quick-add' : 'managed-product-form',
          reason: detail.reason,
          groupId: detail.groupId,
          expectedLineCount: detail.expectedLineCount,
          actualLineCount: detail.actualLineCount,
          expectedAmountMinor: detail.expectedAmountMinor,
          actualAmountMinor: detail.actualAmountMinor,
          rollbackConfirmed: detail.rollbackConfirmed,
        };
        Object.keys(diagnostic).forEach((key) => diagnostic[key] === undefined && delete diagnostic[key]);
        this.dispatchEvent(
          new CustomEvent('optionsight:checkout-integrity', {
            bubbles: true,
            detail: diagnostic,
          })
        );
        const log = status === 'failed' || status === 'blocked' ? console.error : console.info;
        log.call(console, '[OptionSight checkout integrity]', diagnostic);
        window.dataLayer?.push?.({ event: 'optionsight_checkout_integrity', ...diagnostic });
      }

      normalizeOptionSightQuickAddResponse(response) {
        if (!Array.isArray(response?.items)) return response;
        const parent = response.items.find((item) => !item?.properties?._optionsight_for) || response.items[0];
        return parent ? { ...response, id: parent.id, key: parent.key } : response;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

    }
  );
}
